/**
 * CrystalReceiver — resonates when Minna uses Calming Resonance (Purr) nearby.
 *
 * The crystal charges over time while Minna purrs within range.
 * Charge decays if she stops or moves away. Once fully charged, the crystal
 * activates. Hidden crystals are only visible under Aura Sight.
 *
 * Visual: emissive glow intensifies as charge increases; a point light
 * brightens in tandem.
 *
 * Emits CRYSTAL_ACTIVATED when charge reaches 100%, CRYSTAL_CHARGED on solve.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { Material } from "@babylonjs/core/Materials/material";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import { InteractiveObject, InteractiveConfig } from "./InteractiveObject";
import { InstantiatedModel } from "../../systems/AssetManager";
import { gameEvents } from "../../core/EventBus";
import { GameEvents } from "../../config/events";
import { COLORS } from "../../config/GameConfig";
import { AccessibilitySystem } from "../../systems/AccessibilitySystem";

export interface CrystalReceiverConfig extends Omit<InteractiveConfig, "size"> {
  size?: Vector3;
  maxCharge?: number;
  /** Charge units gained per second while purring. */
  chargeRate?: number;
  /** Charge units lost per second when not purring. */
  decayRate?: number;
  /** Static art placement; logical position, reveal and charge ranges stay fixed. */
  modelOffset?: Vector3;
  modelScale?: number;
}

export class CrystalReceiver extends InteractiveObject {
  private charge = 0;
  private readonly maxCharge: number;
  private readonly chargeRate: number;
  private readonly decayRate: number;
  private readonly modelOffset: Vector3;
  private readonly modelScale: number;
  private _charging = false;
  /** Seconds accumulated from updateCharge. Drives the idle breathing pulse. */
  private elapsed = 0;

  private crystalMesh: Mesh;
  private crystalMat: StandardMaterial;
  private glowLight: PointLight;
  /**
   * PBRMaterial refs collected from the loaded `crystal_receiver.glb`
   * when it instantiates. The state machine writes charge emissive to
   * both the procedural fallback (`crystalMat`) and every material in
   * this list so the final art responds to Purr charge the same way
   * the procedural octahedron does. Empty when the GLB fails to load.
   */
  private glbCrystalMaterials: PBRMaterial[] = [];

  /**
   * Unsubscribe handle for the ACCESSIBILITY_CHANGED listener.
   * Stored so dispose() can remove it and avoid a listener leak.
   */
  private a11yUnsub: (() => void) | null = null;

  constructor(scene: Scene, config: CrystalReceiverConfig) {
    const size = config.size ?? new Vector3(0.5, 0.9, 0.5);
    super(scene, { ...config, size });

    this.maxCharge = config.maxCharge ?? 100;
    this.chargeRate = config.chargeRate ?? 25;
    this.decayRate = config.decayRate ?? 15;
    this.modelOffset = config.modelOffset?.clone() ?? Vector3.Zero();
    this.modelScale = config.modelScale ?? 1.7;

    // Crystal shape: an octahedron-like polyhedron (use a sphere w/ low tessellation)
    this.crystalMesh = MeshBuilder.CreatePolyhedron(
      `crystal_${config.key}`,
      { type: 1, size: size.x * 0.5 }, // type 1 = octahedron
      scene
    );
    this.crystalMesh.parent = this.rootMesh;
    this.crystalMesh.position = new Vector3(0, size.y * 0.3, 0).addInPlace(this.modelOffset);
    this.crystalMesh.scaling = new Vector3(1, size.y / size.x, 1).scaleInPlace(this.modelScale / 1.7);

    // Crystal material — starts dim, becomes more emissive as charge increases
    this.crystalMat = new StandardMaterial(`crystal_${config.key}_mat`, scene);
    this.crystalMat.diffuseColor = Color3.FromHexString(COLORS.EMERALD);
    this.crystalMat.specularColor = new Color3(0.5, 1.0, 0.5);
    this.crystalMat.emissiveColor = Color3.Black();
    this.crystalMat.alpha = 0.75;
    this.crystalMesh.material = this.crystalMat;

    // Override the base material reference for state-driven changes
    this.material = this.crystalMat;

    // Point light that intensifies with charge
    this.glowLight = new PointLight(
      `crystal_${config.key}_light`,
      this.rootMesh.position.add(new Vector3(0, size.y * 0.3, 0)).addInPlace(this.modelOffset),
      scene
    );
    this.glowLight.diffuse = Color3.FromHexString(COLORS.BIOLUMINESCENT_GREEN);
    this.glowLight.intensity = 0;
    this.glowLight.range = 3;

    // Attempt to load 3D model override (falls back to procedural mesh)
    // Charge emission belongs to this receiver, not to every instance of its
    // cached model. Clone the material set while retaining the file cache.
    this.loadModelOverride("/assets/models/environment/crystal_receiver.glb");

    // Subscribe to accessibility changes so the long-lived glowLight.diffuse
    // and crystalMat.diffuseColor re-tint live when colorblind mode changes.
    this.a11yUnsub = gameEvents.on(GameEvents.ACCESSIBILITY_CHANGED, () => {
      this.retintColors();
    });
    // Apply immediately so a crystal created after a mode change is already tinted.
    this.retintColors();
  }

  /**
   * Call each frame. Pass `purring = true` if Minna is purring within range.
   * @param deltaSeconds Time elapsed since last frame in seconds.
   * @param purring Whether Minna's Calming Resonance is active nearby.
   */
  updateCharge(deltaSeconds: number, purring: boolean): void {
    if (this.state === "solved") return;

    // Tick the idle-pulse clock every frame regardless of charging so the
    // breathing stays smooth across purr interruptions.
    this.elapsed += deltaSeconds;

    if (purring) {
      this._charging = true;
      this.charge = Math.min(this.charge + this.chargeRate * deltaSeconds, this.maxCharge);
    } else {
      this._charging = false;
      this.charge = Math.max(this.charge - this.decayRate * deltaSeconds, 0);
    }

    this.updateVisuals();

    // Fully charged: activate
    if (this.charge >= this.maxCharge && this.state !== "active") {
      this.activate();
    }
  }

  /**
   * Mark the crystal as permanently solved (puzzle complete).
   */
  solveCrystal(): void {
    this.charge = this.maxCharge;
    this.solve();
    this.updateVisuals();
    gameEvents.emit(GameEvents.CRYSTAL_CHARGED, this.key);
  }

  get charging(): boolean {
    return this._charging;
  }

  get chargePercent(): number {
    return this.charge / this.maxCharge;
  }

  get isActive(): boolean {
    return this.state === "active" || this.state === "solved";
  }

  override getHighlightTarget(): AbstractMesh {
    return this.crystalMesh;
  }

  /**
   * Override so the base class's readability write (brass/copper/black
   * emissive) doesn't stomp on the bioluminescent-green charge feedback.
   * The scene shell re-applies readability every frame for the whole
   * interactive set, which was silently overwriting the revealed idle
   * pulse from `updateVisuals()`. Super still runs so alpha + other
   * readability cues still behave, then we re-run updateVisuals to
   * re-assert the crystal's own emissive state.
   */
  override setReadabilityState(state: "suppressed" | "hint" | "focus" | "active" | "solved"): void {
    super.setReadabilityState(state);
    // Suppressed state still intentionally dark — the tutorial hides the
    // crystal behind Aura Sight + narrative gating at those stages. Only
    // re-apply the green pulse when the object is meant to be readable.
    if (state !== "suppressed") {
      this.updateVisuals();
    }
  }

  dispose(): void {
    // Remove the a11y listener before disposing resources so no callbacks
    // fire on the disposed light or material (leak guard).
    if (this.a11yUnsub) {
      this.a11yUnsub();
      this.a11yUnsub = null;
    }
    this.glowLight.dispose();
    super.dispose();
  }

  /**
   * Re-apply the current colorblind palette to the long-lived diffuse colors.
   * Called once at construction (via ACCESSIBILITY_CHANGED) and on every
   * subsequent mode change. NullEngine-safe — only reads from AccessibilitySystem.
   */
  private retintColors(): void {
    const a11y = AccessibilitySystem.getInstance();
    const lightHex = a11y.resolveColor("BIOLUMINESCENT_GREEN", COLORS.BIOLUMINESCENT_GREEN);
    this.glowLight.diffuse = Color3.FromHexString(lightHex);
    const matHex = a11y.resolveColor("EMERALD", COLORS.EMERALD);
    this.crystalMat.diffuseColor = Color3.FromHexString(matHex);
  }

  /**
   * Collect PBR material refs from the loaded `crystal_receiver.glb` so
   * the charge state machine can drive the final art's emissive, not
   * just the hidden procedural octahedron. Meshes named `CR_Crystal*`
   * own the shard material; everything else (pedestal, brass ring) is
   * left alone — those should stay static because they don't charge.
   *
   * The asset manager clones materials per-instance by default so
   * writes here don't bleed across repeated CrystalReceiver instances
   * in a multi-receiver level.
   */
  protected override onModelLoaded(instance: InstantiatedModel): void {
    // Preserve the existing 1.7x prominence by default. Levels can mount
    // the art at a smaller scale/depth without moving the logical target.
    // scaleInPlace preserves glTF's handedness conversion.
    for (const root of instance.rootNodes) {
      root.scaling.scaleInPlace(this.modelScale);
      root.position.addInPlace(this.modelOffset);
    }

    const seen = new Set<Material>();
    for (const mesh of instance.meshes) {
      const mat = (mesh as { material?: Material | null }).material;
      if (!mat || seen.has(mat)) continue;
      if (mesh.name.includes("CR_Crystal")) {
        seen.add(mat);
        if (mat instanceof PBRMaterial) {
          this.glbCrystalMaterials.push(mat);
        }
      } else if (mesh.name.includes("CR_Stone") || mesh.name.includes("CR_Pedestal")) {
        seen.add(mat);
        // Darken the stone pedestal ~20% so the emissive shards own the
        // silhouette. The albedo write is guarded once-per-material:
        // A pedestal material can occur on multiple meshes in this receiver;
        // avoid compounding the scale per mesh.
        if (mat instanceof PBRMaterial) {
          const matMeta = ((mat.metadata ??= {}) as Record<string, unknown>);
          if (!matMeta.__crPedestalDimmed) {
            matMeta.__crPedestalDimmed = true;
            mat.albedoColor = mat.albedoColor.scale(0.8);
          }
        }
      }
    }
    // Immediately apply the current charge state to the freshly loaded
    // art so the first rendered frame matches the procedural fallback
    // (no visible jump when the GLB swaps in mid-scene).
    this.updateVisuals();
  }

  // ─── Private ─────────────────────────────────────────────────────────

  private activate(): void {
    this.state = "active";

    gameEvents.emit(GameEvents.CRYSTAL_ACTIVATED, this.key);
  }

  private updateVisuals(): void {
    const pct = this.charge / this.maxCharge;
    const isIdle = pct < 0.01;

    // Idle breathing pulse — a ~0.5 Hz sine swing around the baseline so
    // a revealed-but-uncharged receiver reads as "alive, waiting on you"
    // instead of just a dim mesh. When the player starts purring and the
    // charge bar moves, the pulse fades out in favor of the ramp.
    const idlePulse = isIdle ? Math.sin(this.elapsed * 2.5) * 0.08 : 0;

    // Emissive glow scales with charge, but never fully dark — a hidden
    // crystal that's been revealed by Aura Sight still needs to read as
    // "interactive receiver, not scenery" at 0% charge. Floor raised
    // 0.18 → 0.25 after QA flagged the atrium low-ambient pass, then
    // 0.25 → 0.35 in the Q-036e prominence pass (shards were still
    // sub-readable at gameplay zoom).
    const baseGreen = Color3.FromHexString(COLORS.BIOLUMINESCENT_GREEN);
    const emissiveScale = 0.35 + pct * 0.63 + idlePulse;
    this.crystalMat.emissiveColor = baseGreen.scale(emissiveScale);

    // Alpha ranges from 0.65 (revealed but empty) to 1.0 (full). The
    // previous 0.5 floor made the crystal read as a ghost shape even
    // when Aura Sight was active.
    const alpha = 0.65 + pct * 0.35;
    this.crystalMat.alpha = alpha;

    // Drive the loaded GLB's PBR shard materials in parallel with the
    // procedural fallback. StandardMaterial and PBRMaterial both expose
    // `.emissiveColor` + `.alpha`, but PBR additionally honors
    // `emissiveIntensity` (multiplies the color) which we piggy-back on
    // to amplify the pulse through the tonemapper. Without this wiring
    // the loaded crystal mesh holds the GLB-embedded static emissive
    // and the Purr charge is invisible on the final art.
    if (this.glbCrystalMaterials.length > 0) {
      // Intensity floor raised 1.0 → 1.6 (Q-036e) so the revealed-idle
      // shards punch through the tonemapper at gameplay zoom; the charge
      // ramp narrows to keep the full-charge peak in the same range.
      const pbrEmissive = baseGreen.scale(Math.max(0.1, emissiveScale));
      const pbrIntensity = 1.6 + pct * 1.2 + (isIdle ? idlePulse * 2 : 0);
      for (const mat of this.glbCrystalMaterials) {
        mat.emissiveColor = pbrEmissive;
        mat.emissiveIntensity = pbrIntensity;
        mat.alpha = alpha;
        // Enable alpha blending once so the shards blend correctly
        // whenever alpha drops below 1. Leaving this flipped on all the
        // time is fine for a small handful of crystal shards.
        if (mat.transparencyMode !== Material.MATERIAL_ALPHABLEND) {
          mat.transparencyMode = Material.MATERIAL_ALPHABLEND;
        }
      }
    }

    // Point light intensity — also floored so the receiver casts a faint
    // rim light as soon as it's revealed. Mirror the idle pulse at a
    // smaller amplitude so the floor swell reads on the surrounding mesh.
    const lightPulse = isIdle ? Math.sin(this.elapsed * 2.5) * 0.15 : 0;
    this.glowLight.intensity = 0.3 + pct * 1.7 + lightPulse;

    // Update light position to follow root mesh
    this.glowLight.position = this.rootMesh.getAbsolutePosition().add(
      new Vector3(0, this.config.size.y * 0.3, 0).addInPlace(this.modelOffset)
    );
  }
}
